/**
 * Centralized API client for the Twinbooks Dashboard
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6200'

/**
 * API client with built-in error handling and authentication
 */
class ApiClient {
  constructor(baseUrl) {
    this.baseUrl = baseUrl
    this.isOnline = true
    this.onlineStatusListeners = new Set()
    this.onUnauthorizedCallback = null
  }

  /**
   * Set a callback to be called when a 401 Unauthorized response is received
   * @param {function} callback - Callback function to handle logout
   */
  setOnUnauthorized(callback) {
    this.onUnauthorizedCallback = callback
  }

  /**
   * Get the authentication token from localStorage
   */
  getToken() {
    return localStorage.getItem('auth_token')
  }

  /**
   * Subscribe to online status changes
   * @param {function} listener - Callback function(isOnline: boolean)
   * @returns {function} Unsubscribe function
   */
  onOnlineStatusChange(listener) {
    this.onlineStatusListeners.add(listener)
    return () => this.onlineStatusListeners.delete(listener)
  }

  /**
   * Update online status and notify listeners
   */
  setOnlineStatus(isOnline) {
    if (this.isOnline !== isOnline) {
      this.isOnline = isOnline
      this.onlineStatusListeners.forEach(listener => listener(isOnline))
    }
  }

  /**
   * Check if the API is reachable
   * @returns {Promise<boolean>}
   */
  async checkHealth() {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000), // 5 second timeout
      })
      const isOnline = response.ok
      this.setOnlineStatus(isOnline)
      return isOnline
    } catch {
      this.setOnlineStatus(false)
      return false
    }
  }

  /**
   * Make an authenticated API request
   * @param {string} endpoint - API endpoint (e.g., '/api/v1/me')
   * @param {object} options - Fetch options
   * @returns {Promise<Response>}
   */
  async fetch(endpoint, options = {}) {
    const token = this.getToken()

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      })

      // Update online status on successful request
      this.setOnlineStatus(true)

      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401 && this.onUnauthorizedCallback) {
        this.onUnauthorizedCallback()
      }

      return response
    } catch (error) {
      // Check if it's a network error
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        this.setOnlineStatus(false)
      }
      throw error
    }
  }

  /**
   * Make a GET request
   */
  async get(endpoint, options = {}) {
    return this.fetch(endpoint, { ...options, method: 'GET' })
  }

  /**
   * Make a POST request
   */
  async post(endpoint, data, options = {}) {
    return this.fetch(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Make a PATCH request
   */
  async patch(endpoint, data, options = {}) {
    return this.fetch(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Make a PUT request
   */
  async put(endpoint, data, options = {}) {
    return this.fetch(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  /**
   * Make a DELETE request
   */
  async delete(endpoint, options = {}) {
    return this.fetch(endpoint, { ...options, method: 'DELETE' })
  }
}

// Export singleton instance
export const api = new ApiClient(API_URL)

// Export the base URL for cases where it's needed directly
export { API_URL }
